// src/components/staff/StaffForm.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import {
    Typography, MenuItem, Button, Box, TextField, Select,
    Switch, Stack, Tabs, Tab, IconButton, Paper, Fade,
    LinearProgress, InputAdornment, Alert, Snackbar, Avatar, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Chip, FormControlLabel, Tooltip
} from "@mui/material";

import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Badge as BadgeIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    AssignmentInd as AssignmentIndIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    CloudUpload as UploadIcon,
    Security as SecurityIcon,
    EventNote as EventNoteIcon,
    VerifiedUser as VerifiedUserIcon,
    WorkOutline as WorkIcon,
    Assessment as AssessmentIcon,
    Category as CategoryIcon,
    Schedule as ScheduleIcon,
    School as SchoolIcon,
    Edit as EditIcon,
    Public as PublicIcon
} from "@mui/icons-material";
import AppHeader from '../AppHeader';
import NRCSelector from '../common/NRCSelector';
import FormSection from './components/FormSection';
import { employeeIdChipStyle, getTextFieldStyle, selectStyle, toInputDate, buildHierarchicalList } from './constants';

const StaffForm = ({ setIsLoggedIn }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    // prefillStaffId is set when navigating from StaffDetails Rehire button
    const prefillStaffId = location.state?.prefillStaffId || null;
    const isRehireMode = Boolean(prefillStaffId) && !id;
    const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);
    const [currentUserRole, setCurrentUserRole] = useState(null);

    const [permissions, setPermissions] = useState([]);

    useEffect(() => {
        const storedUser = sessionStorage.getItem("user");
        if (storedUser) {
            const u = JSON.parse(storedUser);
            setCurrentUserRole(u.role);
            setPermissions(u.permissions || []);

            // Block users without MANAGE_STAFF permission (except Admins/HR)
            const r = u.role?.toLowerCase();
            const hasEditPermission = u.permissions?.includes('MANAGE_STAFF') || ['super_admin', 'support_admin', 'hr_manager', 'hr', 'hr_branch'].includes(r);

            if (!hasEditPermission) {
                navigate('/staff');
            }
        }
    }, [navigate]);

    const isPayrollAllowed = useMemo(() => {
        const r = currentUserRole?.toLowerCase();
        return permissions.includes("ACCESS_PAYROLL") || r === "super_admin" || r === "support_admin";
    }, [currentUserRole, permissions]);
    const showPayrollTab = isPayrollAllowed && Boolean(id);

    // UI State
    const [confirmPassword, setConfirmPassword] = useState("");
    const [baseline, setBaseline] = useState(null);
    const [discardOpen, setDiscardOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const formSections = ['personal', 'assignment', 'access', 'schedule', ...(showPayrollTab ? ['payroll'] : []), 'documents', 'qualifications'];
    const sectionLabels = {
        personal: 'Personal',
        assignment: 'Assignment',
        access: 'Access & Policy',
        schedule: 'Schedule',
        payroll: 'Payroll',
        documents: 'Documents',
        qualifications: 'Qualifications'
    };
    const [selectedSection, setSelectedSection] = useState(location.state?.section || 'personal');
    const tabValue = Math.max(0, formSections.indexOf(selectedSection));
    const setTabValue = (index) => setSelectedSection(formSections[index]);
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [loading, setLoading] = useState(true);
    const [originalSalary, setOriginalSalary] = useState(0);
    const [payrollDirty, setPayrollDirty] = useState(false);

    // Qualifications state
    const [qualifications, setQualifications] = useState([]);
    const [qualificationDialogOpen, setQualificationDialogOpen] = useState(false);
    const [editingQualIndex, setEditingQualIndex] = useState(null);
    const [editingQualId, setEditingQualId] = useState(null);
    const [qualSaving, setQualSaving] = useState(false);
    const [qualificationForm, setQualificationForm] = useState({
        category: 'ACADEMIC',
        level: '',
        major_subject: '',
        field_of_study: '',
        institution_name: '',
        start_date: '',
        end_date: '',
        status: 'COMPLETED',
        grade_result: '',
        cgpa: '',
        credential_id: '',
        is_overseas: false,
        country: 'Myanmar',
        has_expiry: false,
        expiry_date: '',
        remarks: ''
    });

    const categoryColors = {
        ACADEMIC: { bg: '#e0e7ff', text: '#3730a3', label: 'Academic Degree' },
        DIPLOMA: { bg: '#fef3c7', text: '#92400e', label: 'Diploma' },
        CERTIFICATION: { bg: '#dcfce7', text: '#166534', label: 'Certification' },
        TRAINING: { bg: '#f3e8ff', text: '#6b21a8', label: 'Training' },
        LICENSE: { bg: '#fee2e2', text: '#991b1b', label: 'License' },
        OTHER: { bg: '#f1f5f9', text: '#475569', label: 'Other' },
    };

    const statusColors = {
        COMPLETED: { bg: '#dcfce7', text: '#166534', label: 'Completed' },
        PURSUING: { bg: '#dbeafe', text: '#1e40af', label: 'Pursuing' },
        INCOMPLETE: { bg: '#f1f5f9', text: '#64748b', label: 'Incomplete' }
    };

    const handleOpenAddQualification = () => {
        setQualificationForm({
            category: 'ACADEMIC',
            level: '',
            major_subject: '',
            field_of_study: '',
            institution_name: '',
            start_date: '',
            end_date: '',
            status: 'COMPLETED',
            grade_result: '',
            cgpa: '',
            credential_id: '',
            is_overseas: false,
            country: 'Myanmar',
            has_expiry: false,
            expiry_date: '',
            remarks: ''
        });
        setEditingQualIndex(null);
        setEditingQualId(null);
        setQualificationDialogOpen(true);
    };

    const handleOpenEditQualification = (item, index) => {
        setQualificationForm({
            category: item.category || 'ACADEMIC',
            level: item.level || '',
            major_subject: item.major_subject || '',
            field_of_study: item.field_of_study || '',
            institution_name: item.institution_name || '',
            start_date: toInputDate(item.start_date) || '',
            end_date: toInputDate(item.end_date) || '',
            status: item.status || 'COMPLETED',
            grade_result: item.grade_result || '',
            cgpa: item.cgpa != null ? String(item.cgpa) : '',
            credential_id: item.credential_id || '',
            is_overseas: Boolean(item.is_overseas),
            country: item.country || 'Myanmar',
            has_expiry: Boolean(item.has_expiry),
            expiry_date: toInputDate(item.expiry_date) || '',
            remarks: item.remarks || ''
        });
        setEditingQualIndex(index);
        setEditingQualId(item.id || null);
        setQualificationDialogOpen(true);
    };

    const handleDeleteQualification = async (index, item) => {
        if (id && item.id) {
            if (!window.confirm(`Delete qualification "${item.level} - ${item.institution_name}"?`)) return;
            try {
                const h = { 'X-Tenant': tenant };
                await axios.delete(`${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/qualifications/${item.id}`, { withCredentials: true, headers: h });
                setQualifications(prev => prev.filter((_, i) => i !== index));
                setSnackbar({ open: true, message: 'Qualification deleted successfully', severity: 'success' });
            } catch (err) {
                setSnackbar({ open: true, message: err.response?.data?.message || err.message, severity: 'error' });
            }
        } else {
            setQualifications(prev => prev.filter((_, i) => i !== index));
            setSnackbar({ open: true, message: 'Qualification removed from list', severity: 'info' });
        }
    };

    const handleSaveQualificationDialog = async () => {
        if (!qualificationForm.level?.trim() || !qualificationForm.institution_name?.trim()) {
            setSnackbar({ open: true, message: 'Qualification Level and Institution Name are required', severity: 'warning' });
            return;
        }

        const payload = {
            ...qualificationForm,
            level: qualificationForm.level.trim(),
            institution_name: qualificationForm.institution_name.trim(),
            field_of_study: qualificationForm.field_of_study?.trim() || null,
            major_subject: qualificationForm.major_subject?.trim() || null,
            country: qualificationForm.country?.trim() || 'Myanmar',
            grade_result: qualificationForm.grade_result?.trim() || null,
            credential_id: qualificationForm.credential_id?.trim() || null,
            remarks: qualificationForm.remarks?.trim() || null,
            is_overseas: qualificationForm.is_overseas ? 1 : 0,
            has_expiry: qualificationForm.has_expiry ? 1 : 0,
            cgpa: qualificationForm.cgpa ? parseFloat(qualificationForm.cgpa) : null
        };

        if (id) {
            setQualSaving(true);
            try {
                const h = { 'X-Tenant': tenant };
                if (editingQualId) {
                    await axios.put(`${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/qualifications/${editingQualId}`, payload, { withCredentials: true, headers: h });
                } else {
                    await axios.post(`${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/qualifications`, payload, { withCredentials: true, headers: h });
                }
                const qRes = await axios.get(`${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/qualifications`, { withCredentials: true, headers: h });
                if (qRes.data?.success) setQualifications(qRes.data.data);
                setQualificationDialogOpen(false);
                setSnackbar({ open: true, message: editingQualId ? 'Qualification updated successfully' : 'Qualification added successfully', severity: 'success' });
            } catch (err) {
                setSnackbar({ open: true, message: err.response?.data?.message || err.message, severity: 'error' });
            } finally {
                setQualSaving(false);
            }
        } else {
            if (editingQualIndex !== null) {
                setQualifications(prev => {
                    const next = [...prev];
                    next[editingQualIndex] = payload;
                    return next;
                });
            } else {
                setQualifications(prev => [...prev, payload]);
            }
            setQualificationDialogOpen(false);
            setSnackbar({ open: true, message: 'Qualification staged for registration', severity: 'info' });
        }
    };

    // Data State
    const [isAddingNewAssignment, setIsAddingNewAssignment] = useState(false);
    const [lookups, setLookups] = useState({
        locations: [], managers: [], hrs: [], smGroups: [],
        smPatterns: [], otRateProfiles: [], orgNodes: [], orgTypes: [], somBranches: [],
        gradeSets: [], grades: [], jobs: [], jobRoles: [],
        late_policies: [], calc_bases: [], payrollElements: [], somConfig: null,
        nationalities: [], religions: [], races: []
    });


    const [staff, setStaff] = useState({
        full_name: '', salutation: '', staff_code: '', employee_number: '', department: '',
        father_name: '', nationality: 'Burmese', religion: 'Buddhist', race: '',
        marital_status: 'SINGLE', blood_type: '',
        employment_status: 'PROBATION', joining_date: '',
        confirmation_date: '', cessation_date: '',
        allowed_locations: [], username: '', email: '', role: 'staff',
        password: '', is_active: 1, work_type: 'office',
        allow_split_shift: 0,
        ot_eligible: 1, ot_policy_code: 'MANUAL_CLAIM', ot_rate_profile: '',
        auto_approve_manual_attendance: 0, is_photo_required: 1, level1_approver_id: '',
        web_clock_allowed: 0, web_geofence_required: 0, exclude_from_payroll: 0,
        level2_approver_id: '', sm_group_id: '', org_node_id: '',
        branch_id: '', grade_id: '', job_id: '', job_role_id: '',
        late_policy_id: '', gender: 'MALE', date_of_birth: '',
        sm_pattern_id: '', nrc_number: '', passport_number: '', social_security_no: '',
        personal_phone: '', permanent_address: '', city_township: '', country: '', postcode: '', correspondence_address: '', remarks: '', emergency_contact_name: '',
        emergency_contact_phone: '', bank_name: '', bank_account_no: '',
        relief_spouse: 0, relief_children_count: 0, relief_parents_count: 0,
        basic_salary: 0, calculation_basis_id: '', payroll_group_id: '',
        ssb_enabled: 1, pit_enabled: 1, ssb_scheme_override: 'INHERIT', statutory_reason: '',
        effective_from_payroll: toInputDate(new Date()), recurring_elements: [],
        sm_effective_from: toInputDate(new Date()),
        personnel_effective_from: toInputDate(new Date()),
        assignment_reason: 'CORRECTION',
        payroll_change_reason: 'CORRECTION',
        assignment_notes: ''
    });

    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState("/default-avatar.png");
    const hasActiveAssignment = Boolean(staff.branch_id || staff.job_role_id || staff.org_node_id);
    // Qualifications on an existing employee are saved independently.
    const snapshot = JSON.stringify({ staff, confirmPassword, isAddingNewAssignment, qualifications: id ? null : qualifications });
    const hasUnsavedChanges = baseline !== null && (snapshot !== baseline || Boolean(photoFile));

    useEffect(() => {
        if (!loading && baseline === null) setBaseline(snapshot);
    }, [loading, baseline, snapshot]);

    useEffect(() => {
        if (!hasUnsavedChanges) return;
        const warnBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warnBeforeUnload);
        return () => window.removeEventListener('beforeunload', warnBeforeUnload);
    }, [hasUnsavedChanges]);

    const returnToEmployee = () => navigate(id ? `/staff/${id}` : '/staff', {
        state: id ? { section: location.state?.section === 'disciplinary' ? 'disciplinary' : formSections[tabValue] } : null
    });
    const handleCancel = () => {
        if (saving) return;
        if (hasUnsavedChanges) setDiscardOpen(true);
        else returnToEmployee();
    };


    const validate = useCallback(() => {
        let e = {};
        if (!staff.full_name?.trim()) e.full_name = "Required";
        if (!staff.staff_code?.trim()) e.staff_code = "Required";
        if (!staff.username?.trim()) e.username = "Required";
        if (staff.email && !/\S+@\S+\.\S+/.test(staff.email)) e.email = "Invalid Email";
        // In rehire mode, password is auto-reset server-side — no client validation needed
        if (!id && !isRehireMode && (!staff.password || staff.password.length < 6)) e.password = "Min 6 chars";
        if (staff.password && staff.password !== confirmPassword) e.confirmPassword = "Mismatch";

        if (showPayrollTab && hasActiveAssignment && !staff.has_pending_payroll && !isAddingNewAssignment) {
            if (staff.basic_salary === '' || Number(staff.basic_salary) <= 0) {
                e.basic_salary = "Salary must be greater than 0";
            }
        }

        setErrors(e);

        return e;
    }, [staff, confirmPassword, id, isRehireMode, showPayrollTab, hasActiveAssignment, isAddingNewAssignment]);

    useEffect(() => {
        if (currentUserRole === null) return;
        const fetchAll = async () => {
            try {
                const h = { 'X-Tenant': tenant };

                // Define common endpoints
                const endpoints = [
                    ['locations', `${process.env.REACT_APP_LOCATION_API_URL}/locations`],
                    ['managers', `${process.env.REACT_APP_API_URL1}/users/users?role=manager,management_viewer,hr,hr_manager`],
                    ['hrs', `${process.env.REACT_APP_API_URL1}/users/users?role=hr,hr_manager`],
                    ['smGroups', `${process.env.REACT_APP_API_URL1}/sm/groups`],
                    ['smPatterns', `${process.env.REACT_APP_API_URL1}/sm/patterns`],
                    ['otRateProfiles', `${process.env.REACT_APP_API_URL1}/sm/ot-profiles`],
                    ['orgNodes', `${process.env.REACT_APP_API_URL1}/som/nodes`],
                    ['orgTypes', `${process.env.REACT_APP_API_URL1}/som/types`],
                    ['somConfig', `${process.env.REACT_APP_API_URL1}/som/config`],
                    ['somBranches', `${process.env.REACT_APP_API_URL1}/som/branches`],
                    ['grades', `${process.env.REACT_APP_API_URL1}/som/grades`],
                    ['jobs', `${process.env.REACT_APP_API_URL1}/som/jobs`],
                    ['jobRoles', `${process.env.REACT_APP_API_URL1}/som/roles`],
                    ['latePolicies', `${process.env.REACT_APP_API_URL1}/sm/late-policies`],
                    ['nationalities', `${process.env.REACT_APP_API_URL1}/sm/nationalities`],
                    ['religions', `${process.env.REACT_APP_API_URL1}/sm/religions`],
                    ['races', `${process.env.REACT_APP_API_URL1}/sm/races`]
                ];

                // Add payroll endpoints only if allowed
                if (isPayrollAllowed) {
                    endpoints.push(['calcBases', `${process.env.REACT_APP_API_URL1}/sm/payroll/calc-bases`]);
                    endpoints.push(['payrollElements', `${process.env.REACT_APP_API_URL1}/sm/payroll/elements`]);
                    endpoints.push(['payrollSettings', `${process.env.REACT_APP_API_URL1}/sm/payroll/settings`]);
                    endpoints.push(['payrollGroups', `${process.env.REACT_APP_API_URL1}/sm/payroll-groups`]);
                }

                // Fetch all and handle individual errors
                const results = await Promise.all(endpoints.map(e =>
                    axios.get(e[1], { withCredentials: true, headers: h })
                        .catch(err => {
                            console.warn(`Lookup failed for ${e[0]}:`, err.message);
                            return null;
                        })
                ));

                const newLookups = {};
                results.forEach((r, i) => {
                    const key = endpoints[i][0];
                    let data = r?.data?.data || r?.data || [];
                    if (key === 'orgNodes') {
                        data = buildHierarchicalList(data);
                    }
                    newLookups[key] = data;
                });

                // --- DATA SCOPING (Implicit Model) ---
                const sessionUserStr = sessionStorage.getItem("user");
                if (sessionUserStr) {
                    const user = JSON.parse(sessionUserStr);
                    const scoping = user.adminScoping;
                    if (scoping && !scoping.isGlobal) {
                        // Filter branches
                        if (scoping.branches && scoping.branches.length > 0) {
                            newLookups.somBranches = newLookups.somBranches.filter(b => scoping.branches.includes(b.id));
                        }
                        // Filter nodes
                        if (scoping.nodes && scoping.nodes.length > 0) {
                            newLookups.orgNodes = newLookups.orgNodes.filter(n => scoping.nodes.includes(n.id));
                        }
                    }
                }

                setLookups(newLookups);

                if (!id && isPayrollAllowed && Array.isArray(newLookups.payrollSettings)) {
                    const payrollSettings = newLookups.payrollSettings.reduce(
                        (acc, item) => ({ ...acc, [item.setting_key]: item.setting_value }),
                        {}
                    );
                    setStaff(prev => ({
                        ...prev,
                        ssb_enabled: payrollSettings.SSB_DEFAULT_ENABLED === '0' ? 0 : 1,
                        pit_enabled: payrollSettings.PIT_DEFAULT_ENABLED === '0' ? 0 : 1
                    }));
                }

                if (id) {
                    // --- EDIT MODE: load existing staff ---
                    const [sRes, pRes, dRes, qRes] = await Promise.all([
                        axios.get(`${process.env.REACT_APP_STAFF_API_URL}/staff/${id}`, { withCredentials: true, headers: h }),
                        isPayrollAllowed
                            ? axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/staff/${id}`, { withCredentials: true, headers: h })
                            : Promise.resolve({ data: { success: true, data: null } }),
                        axios.get(`${process.env.REACT_APP_API_URL1}/personnel/staff/${id}/documents`, { withCredentials: true, headers: h }),
                        axios.get(`${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/qualifications`, { withCredentials: true, headers: h }).catch(() => ({ data: { data: [] } }))
                    ]);
                    if (qRes?.data?.data) {
                        setQualifications(qRes.data.data);
                    }
                    const s = sRes.data;
                    const p = pRes.data?.data;
                    const loadedSalary = p?.profile?.basic_salary || 0;
                    setOriginalSalary(loadedSalary);
                    setStaff(prev => ({
                        ...prev, ...s,
                        joining_date: toInputDate(s.joining_date),
                        confirmation_date: toInputDate(s.confirmation_date),
                        personnel_effective_from: toInputDate(s.personnel_effective_from) || toInputDate(new Date()),
                        cessation_date: toInputDate(s.cessation_date),
                        date_of_birth: toInputDate(s.date_of_birth),
                        basic_salary: loadedSalary,
                        calculation_basis_id: p?.profile?.calculation_basis_id || '',
                        payroll_group_id: p?.profile?.payroll_group_id || '',
                        ssb_enabled: Number(p?.statutory?.ssb_enabled ?? 1),
                        pit_enabled: Number(p?.statutory?.pit_enabled ?? 1),
                        ssb_scheme_override: p?.statutory?.ssb_scheme_override || 'INHERIT',
                        statutory_reason: p?.statutory?.reason || '',
                        effective_from_payroll: toInputDate(p?.profile?.effective_from) || toInputDate(new Date()),
                        payroll_change_reason: p?.profile?.payroll_change_reason || 'CORRECTION',
                        recurring_elements: p?.recurring || []
                    }));

                    const photoDoc = dRes.data?.data?.find(d => d.document_type === 'PROFILE_PHOTO');
                    if (photoDoc) {
                        setPhotoPreview(`${process.env.REACT_APP_API_URL1}/admin/documents/${photoDoc.dms_id}/preview?tenant=${tenant}`);
                    }
                } else if (prefillStaffId) {
                    // --- REHIRE MODE: load personal data from previous employment ---
                    const { data: prefillRes } = await axios.get(
                        `${process.env.REACT_APP_STAFF_API_URL}/staff/${prefillStaffId}/rehire-prefill`,
                        { withCredentials: true, headers: h }
                    );
                    const p = prefillRes.data;
                    setStaff(prev => ({
                        ...prev,
                        // Pre-fill personal fields
                        full_name: p.full_name || '',
                        salutation: p.salutation || '',
                        father_name: p.father_name || '',
                        nationality: p.nationality || 'Burmese',
                        religion: p.religion || 'Buddhist',
                        race: p.race || '',
                        gender: p.gender || 'MALE',
                        date_of_birth: toInputDate(p.date_of_birth),
                        marital_status: p.marital_status || 'SINGLE',
                        blood_type: p.blood_type || '',
                        nrc_number: p.nrc_number || '',
                        passport_number: p.passport_number || '',
                        social_security_no: p.social_security_no || '',
                        personal_phone: p.personal_phone || '',
                        permanent_address: p.permanent_address || '',
                        emergency_contact_name: p.emergency_contact_name || '',
                        emergency_contact_phone: p.emergency_contact_phone || '',
                        bank_name: p.bank_name || '',
                        bank_account_no: p.bank_account_no || '',
                        relief_spouse: p.relief_spouse || 0,
                        relief_children_count: p.relief_children_count || 0,
                        relief_parents_count: p.relief_parents_count || 0,
                        // Pre-fill credentials (username/email are read-only)
                        username: p.username || '',
                        email: p.email || '',
                        // New employment fields stay blank
                        staff_code: p.staff_code || '',
                        employee_number: '',
                        joining_date: '',
                        employment_status: 'PROBATION',
                        // Rehire hidden field
                        previous_staff_id: p.previous_staff_id,
                    }));
                    if (p.qualifications && Array.isArray(p.qualifications)) {
                        setQualifications(p.qualifications);
                    }
                }
            } catch (err) { console.error(err); } finally { setLoading(false); }
        };
        fetchAll();
    }, [id, tenant, isPayrollAllowed, currentUserRole, prefillStaffId]);

    useEffect(() => { validate(); }, [validate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (new Set([
            'basic_salary', 'calculation_basis_id', 'payroll_group_id', 'effective_from_payroll',
            'payroll_change_reason', 'ssb_scheme_override', 'statutory_reason'
        ]).has(name)) setPayrollDirty(true);
        setStaff(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'work_type' && value !== 'office') {
                updated.allow_split_shift = 0;
            }
            if (name === 'basic_salary') {
                const newVal = Number(value || 0);
                const oldVal = Number(originalSalary);
                if (newVal > oldVal) {
                    updated.payroll_change_reason = 'INCREMENT';
                } else if (newVal < oldVal) {
                    updated.payroll_change_reason = 'DECREMENT';
                } else {
                    updated.payroll_change_reason = 'CORRECTION';
                }
            }
            return updated;
        });
        setTouched(prev => ({ ...prev, [name]: true }));
    };

    const handlePhotoChange = async (e) => {
        let file = e.target.files[0];
        if (file) {
            try {
                if (file.type.startsWith('image/')) {
                    // Compress to max 1024x1024 for profile photo to reduce size
                    const options = { maxSizeMB: 1, maxWidthOrHeight: 1024, useWebWorker: true };
                    file = await imageCompression(file, options);
                }
            } catch (err) {
                console.error('Compression error:', err);
            }
            setPhotoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setPhotoPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Run validation synchronously to capture latest errors
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            // Mark all fields with errors as touched
            const newTouched = {};
            Object.keys(validationErrors).forEach(field => {
                newTouched[field] = true;
            });
            setTouched(prev => ({ ...prev, ...newTouched }));

            // Move directly to the section containing the first validation error.
            if (validationErrors.full_name || validationErrors.staff_code) {
                setSelectedSection('personal');
            } else if (validationErrors.username || validationErrors.email || validationErrors.password || validationErrors.confirmPassword) {
                setSelectedSection('access');
            } else if (validationErrors.basic_salary) {
                setSelectedSection('payroll');
            }

            setSnackbar({
                open: true,
                message: "Please fix validation errors before submitting.",
                severity: "error"
            });
            return;
        }

        setSaving(true);
        try {
            const h = { 'X-Tenant': tenant };
            const payload = {
                ...staff,
                qualifications: qualifications,
                isAddingNewAssignment: isAddingNewAssignment,
                allowed_locations: (staff.allowed_locations || []).map(item => {
                    if (typeof item === 'number') return item;
                    return (lookups.locations || []).find(l => l.location_name === item)?.id;
                }).filter(Boolean)
            };

            let staffId = id;
            if (id) {
                // EDIT MODE
                await axios.put(`${process.env.REACT_APP_STAFF_API_URL}/staff/${id}`, payload, { withCredentials: true, headers: h });
            } else if (isRehireMode) {
                // REHIRE MODE — calls /staff/rehire, no password in payload (server resets it)
                const rehirePayload = {
                    ...payload,
                    previous_staff_id: staff.previous_staff_id,
                    assignment_reason: 'REHIRE',
                };
                const res = await axios.post(`${process.env.REACT_APP_STAFF_API_URL}/staff/rehire`, rehirePayload, { withCredentials: true, headers: h });
                staffId = res.data.staff_id;
            } else {
                // NEW STAFF MODE
                const res = await axios.post(`${process.env.REACT_APP_STAFF_API_URL}/register-staff`, payload, { withCredentials: true, headers: h });
                staffId = res.data.staff_id || res.data.id || res.data.data?.id;
            }

            if (staffId && id && isPayrollAllowed && payrollDirty && !isAddingNewAssignment && !staff.has_pending_payroll) {
                // Handle Payroll Save
                const pPayload = {
                    basic_salary: Number(staff.basic_salary || 0),
                    calculation_basis_id: staff.calculation_basis_id || null,
                    payroll_group_id: staff.payroll_group_id || null,
                    effective_from: toInputDate(staff.effective_from_payroll),
                    payroll_change_reason: staff.payroll_change_reason || 'CORRECTION',
                    ssb_enabled: Number(staff.ssb_enabled) === 1,
                    pit_enabled: Number(staff.pit_enabled) === 1,
                    ssb_scheme_override: staff.ssb_scheme_override || 'INHERIT',
                    statutory_reason: staff.statutory_reason || null,
                    recurring_elements: (staff.recurring_elements || []).map(re => ({
                        id: re.id || null,
                        element_id: re.element_id,
                        amount: Number(re.amount || 0),
                        effective_from: toInputDate(re.effective_from) || null,
                        effective_to: toInputDate(re.effective_to) || null
                    }))
                };
                await axios.post(`${process.env.REACT_APP_API_URL1}/sm/payroll/staff/${staffId}`, pPayload, { withCredentials: true, headers: h });
            }

            if (staffId) {
                // Handle Photo Upload
                if (photoFile) {
                    const photoFormData = new FormData();
                    photoFormData.append('staff_id', staffId);
                    photoFormData.append('document_type', 'PROFILE_PHOTO');
                    photoFormData.append('notes', 'Staff profile photo');
                    photoFormData.append('file', photoFile);
                    await axios.post(`${process.env.REACT_APP_API_URL1}/personnel/staff/${staffId}/documents`, photoFormData, {
                        withCredentials: true,
                        headers: { ...h, 'Content-Type': 'multipart/form-data' }
                    });
                }
            }

            setSnackbar({ open: true, message: id ? 'Staff profile updated successfully!' : isRehireMode ? 'Staff rehired successfully!' : 'Staff registered successfully!', severity: 'success' });
            navigate(`/staff/${staffId}`, { state: { section: formSections[tabValue], profileSaved: true } });
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.message || err.message || 'Could not save staff records',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };


    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#f8fafc' }}><LinearProgress sx={{ width: 300, borderRadius: 2, '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' } }} /></Box>;

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
            <AppHeader onLogout={() => { localStorage.clear(); setIsLoggedIn(false); navigate('/login'); }} />

            <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: 20, sm: 14 }, maxWidth: 1400, mx: 'auto', width: '100%', boxSizing: 'border-box', flexGrow: 1 }}>
                <Fade in timeout={500}>
                    <form id="staff-form" noValidate onSubmit={handleSubmit}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Box>
                                <Button startIcon={<ArrowBackIcon />} onClick={handleCancel} disabled={saving} sx={{ color: '#64748b', fontWeight: 600, textTransform: 'none', mb: 1 }}>{id ? 'Back to Employee' : 'Back to Directory'}</Button>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                                    {id ? 'Edit Employee' : isRehireMode ? `Rehire — ${staff.full_name || 'Staff'}` : 'Register New Employee'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    {id ? 'Update employee information. Required fields are marked *.' : isRehireMode ? 'Assign new employee ID and fill employment details for the rejoining staff.' : 'Complete the information below to enroll staff in the ecosystem.'}
                                </Typography>
                            </Box>
                        </Box>

                        {id && (
                            <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, mb: 3, borderRadius: 4 }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar src={photoPreview} sx={{ width: 56, height: 56 }} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>{staff.full_name}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {[staff.job_role_name, staff.branch_name].filter(Boolean).join(' • ')}
                                        </Typography>
                                        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} sx={{ mt: 1 }}>
                                            <Chip sx={employeeIdChipStyle} variant="outlined" label={`Personal ID: ${staff.staff_code || '—'}`} />
                                            <Chip sx={employeeIdChipStyle} variant="outlined" label={`Current Employee ID: ${staff.employee_number || 'Not assigned'}`} />
                                        </Stack>
                                    </Box>
                                    <Chip size="small" label="Editing" color="primary" variant="outlined" />
                                </Stack>
                            </Paper>
                        )}
                        {!id && (
                            <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, mb: 3, borderRadius: 4, borderColor: '#c7d2fe', bgcolor: '#eef2ff' }}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#312e81' }}>
                                            Employee identity
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#475569', mt: 0.5 }}>
                                            Personal ID identifies the person permanently. Employee ID identifies this employment period and can change when the person rejoins.
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                                        <Chip sx={employeeIdChipStyle} variant="outlined" label="Personal ID • One per person" />
                                        <Chip sx={employeeIdChipStyle} variant="outlined" label="Employee ID • One per employment" />
                                    </Stack>
                                </Stack>
                            </Paper>
                        )}
                        <Paper sx={{ borderRadius: 4, mb: 4, overflow: 'hidden', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)' }}>
                            <Tabs
                                value={tabValue}
                                onChange={(e, v) => setTabValue(v)}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{
                                    bgcolor: '#fff', borderBottom: '1px solid #f1f5f9',
                                    '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: 'primary.main' },
                                    '& .MuiTab-root': { py: 2.5, fontWeight: 700, color: '#64748b', textTransform: 'none', fontSize: 14, minWidth: 120 },
                                    '& .Mui-selected': { color: '#6366f1 !important' }
                                }}
                            >
                                <Tab icon={<PersonIcon fontSize="small" />} iconPosition="start" label="Personal" />
                                <Tab icon={<BusinessIcon fontSize="small" />} iconPosition="start" label="Assignment" />
                                <Tab icon={<SecurityIcon fontSize="small" />} iconPosition="start" label="Access & Policy" />
                                <Tab icon={<EventNoteIcon fontSize="small" />} iconPosition="start" label="Schedule" />
                                {showPayrollTab && <Tab icon={<AssessmentIcon fontSize="small" />} iconPosition="start" label="Payroll" />}
                                <Tab icon={<AssignmentIndIcon fontSize="small" />} iconPosition="start" label="Documents" />
                                <Tab icon={<SchoolIcon fontSize="small" />} iconPosition="start" label="Qualifications" />
                            </Tabs>

                            <Box sx={{ p: { xs: 1.5, md: 3 }, bgcolor: '#fff' }}>
                                {selectedSection === 'personal' && (
                                    <>
                                        <FormSection title="Identity & Demographics" icon={BadgeIcon}>
                                            <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 3, mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #f1f5f9' }}>
                                                <Avatar
                                                    src={photoPreview}
                                                    sx={{ width: 80, height: 80, fontSize: '1.5rem', fontWeight: 800, bgcolor: '#fff', color: 'primary.main', border: '2px solid #e2e8f0' }}
                                                    imgProps={{ style: { objectPosition: 'center top' } }}
                                                >
                                                    {staff.full_name?.charAt(0) || <PersonIcon />}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b', mb: 0.5 }}>Profile Photo (Optional)</Typography>
                                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1.5 }}>Upload a professional portrait in JPG/PNG format.</Typography>
                                                    <Button variant="contained" component="label" size="small" sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
                                                        {photoFile ? 'Change Photo' : 'Upload Image'}
                                                        <input type="file" hidden accept="image/*" onChange={handlePhotoChange} />
                                                    </Button>
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                <Box sx={{ width: '120px' }}>
                                                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Salutation</Typography>
                                                    <Select fullWidth name="salutation" value={staff.salutation || ''} onChange={handleChange} sx={selectStyle}>
                                                        <MenuItem value=""><em>None</em></MenuItem>
                                                        <MenuItem value="U">U</MenuItem>
                                                        <MenuItem value="Daw">Daw</MenuItem>
                                                        <MenuItem value="ဦး">ဦး</MenuItem>
                                                        <MenuItem value="ဒေါ်">ဒေါ်</MenuItem>
                                                        <MenuItem value="Mr.">Mr.</MenuItem>
                                                        <MenuItem value="Mrs.">Mrs.</MenuItem>
                                                        <MenuItem value="Ms.">Ms.</MenuItem>
                                                        <MenuItem value="Dr.">Dr.</MenuItem>
                                                    </Select>
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Legal Full Name *</Typography>
                                                    <TextField fullWidth name="full_name" required inputProps={{ 'aria-label': 'Legal Full Name' }} placeholder="As shown on NRC/Passport" value={staff.full_name} onChange={handleChange} error={touched.full_name && !!errors.full_name} helperText={touched.full_name && errors.full_name} sx={getTextFieldStyle(touched.full_name, errors.full_name)} />
                                                </Box>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Personal ID *</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="staff_code"
                                                    required
                                                    inputProps={{ 'aria-label': 'Personal ID' }}
                                                    placeholder="e.g. P-000123"
                                                    value={staff.staff_code}
                                                    onChange={handleChange}
                                                    error={touched.staff_code && !!errors.staff_code}
                                                    helperText={touched.staff_code && errors.staff_code ? errors.staff_code : 'Permanent ID for this person. Keep the same ID when rehiring.'}
                                                    disabled={isRehireMode}
                                                    sx={getTextFieldStyle(touched.staff_code, errors.staff_code)}
                                                />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Father's Name</Typography>
                                                <TextField fullWidth name="father_name" placeholder="Full name of father" value={staff.father_name} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Nationality</Typography>
                                                <Select fullWidth name="nationality" value={staff.nationality} onChange={handleChange} sx={selectStyle}>
                                                    {lookups.nationalities.map(n => <MenuItem key={n.id} value={n.name}>{n.name}</MenuItem>)}
                                                </Select>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Religion</Typography>
                                                <Select fullWidth name="religion" value={staff.religion} onChange={handleChange} sx={selectStyle}>
                                                    {lookups.religions.map(r => <MenuItem key={r.id} value={r.name}>{r.name}</MenuItem>)}
                                                </Select>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Race</Typography>
                                                <Select fullWidth name="race" value={staff.race} onChange={handleChange} sx={selectStyle}>
                                                    {lookups.races.map(r => <MenuItem key={r.id} value={r.name}>{r.name}</MenuItem>)}
                                                </Select>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Gender</Typography>
                                                <Select fullWidth name="gender" value={staff.gender || ''} onChange={handleChange} sx={selectStyle}><MenuItem value="MALE">Male</MenuItem><MenuItem value="FEMALE">Female</MenuItem></Select>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Date of Birth</Typography>
                                                <TextField fullWidth type="date" name="date_of_birth" value={staff.date_of_birth || ''} onChange={handleChange} sx={getTextFieldStyle()} InputLabelProps={{ shrink: true }} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Marital Status</Typography>
                                                <Select fullWidth name="marital_status" value={staff.marital_status || ''} onChange={handleChange} sx={selectStyle}>
                                                    <MenuItem value="SINGLE">Single</MenuItem>
                                                    <MenuItem value="MARRIED">Married</MenuItem>
                                                    <MenuItem value="DIVORCED">Divorced</MenuItem>
                                                    <MenuItem value="WIDOWED">Widowed</MenuItem>
                                                </Select>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>NRC / ID</Typography>
                                                <NRCSelector
                                                    value={staff.nrc_number}
                                                    onChange={(val) => setStaff(prev => ({ ...prev, nrc_number: val }))}
                                                />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Passport Number</Typography>
                                                <TextField fullWidth name="passport_number" placeholder="P Exxxxxxx" value={staff.passport_number} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Social Security No</Typography>
                                                <TextField fullWidth name="social_security_no" value={staff.social_security_no} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Blood Type</Typography>
                                                <TextField fullWidth name="blood_type" placeholder="e.g. O+" value={staff.blood_type} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                        </FormSection>
                                        <FormSection title="Contact Details" icon={PersonIcon}>
                                            <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 2' } }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Permanent Address</Typography>
                                                <TextField fullWidth name="permanent_address" multiline rows={2} value={staff.permanent_address} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>City / Township</Typography>
                                                <TextField fullWidth name="city_township" value={staff.city_township || ''} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Country</Typography>
                                                <TextField fullWidth name="country" value={staff.country || ''} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Postcode</Typography>
                                                <TextField fullWidth name="postcode" value={staff.postcode || ''} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Personal Phone</Typography>
                                                <TextField fullWidth name="personal_phone" value={staff.personal_phone} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                            <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 2' } }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Correspondence Address</Typography>
                                                <TextField fullWidth name="correspondence_address" multiline rows={2} value={staff.correspondence_address || ''} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Emergency Contact Name</Typography>
                                                <TextField fullWidth name="emergency_contact_name" value={staff.emergency_contact_name} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Emergency Contact Phone</Typography>
                                                <TextField fullWidth name="emergency_contact_phone" value={staff.emergency_contact_phone} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                            <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 2' } }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Remarks</Typography>
                                                <TextField fullWidth name="remarks" multiline rows={2} value={staff.remarks || ''} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                        </FormSection>
                                        <FormSection title="Bank Information" icon={CategoryIcon}>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Bank Name</Typography>
                                                <TextField fullWidth name="bank_name" value={staff.bank_name} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Bank Account No</Typography>
                                                <TextField fullWidth name="bank_account_no" value={staff.bank_account_no} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                        </FormSection>
                                        <FormSection title="Tax Relief Profile" icon={CategoryIcon}>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Spouse Relief</Typography>
                                                <Select fullWidth name="relief_spouse" value={staff.relief_spouse} onChange={handleChange} sx={selectStyle}>
                                                    <MenuItem value={0}>Not Applicable</MenuItem>
                                                    <MenuItem value={1}>Applicable</MenuItem>
                                                </Select>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Children Count</Typography>
                                                <TextField fullWidth type="number" name="relief_children_count" value={staff.relief_children_count} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Parents Count</Typography>
                                                <TextField fullWidth type="number" name="relief_parents_count" value={staff.relief_parents_count} onChange={handleChange} sx={getTextFieldStyle()} />
                                            </Box>
                                        </FormSection>
                                    </>
                                )}

                                {selectedSection === 'assignment' && (
                                    <>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Assignment Details</Typography>
                                            {Boolean(id) && permissions.includes('MANAGE_STAFF') && (
                                                <Button
                                                    variant={isAddingNewAssignment ? "outlined" : "contained"}
                                                    color={isAddingNewAssignment ? "error" : "primary"}
                                                    startIcon={isAddingNewAssignment ? <DeleteIcon /> : <AddIcon />}
                                                    onClick={() => {
                                                        const entering = !isAddingNewAssignment;
                                                        setIsAddingNewAssignment(entering);
                                                        if (entering) {
                                                            // Clear fields that must NOT be pre-loaded for a new assignment
                                                            setStaff(prev => ({
                                                                ...prev,
                                                                assignment_reason: '',
                                                                assignment_notes: '',
                                                                personnel_effective_from: new Date().toISOString().split('T')[0]
                                                            }));
                                                        }
                                                    }}
                                                    sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
                                                >
                                                    {isAddingNewAssignment ? "Cancel New Assignment" : "Add New Assignment"}
                                                </Button>
                                            )}
                                        </Box>
                                        {isAddingNewAssignment && (
                                            <Alert severity="info" sx={{ mb: 2 }}>
                                                Saving creates an employment assignment in Pending Payroll Setup. Promotion/demotion requires a title or grade change; transfer requires an organization or branch change. Salary-only changes belong in the Payroll tab.
                                            </Alert>
                                        )}
                                        <FormSection title="Organizational Alignment" icon={BusinessIcon}>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Effective From (Role/Org)</Typography>
                                                <TextField fullWidth type="date" name="personnel_effective_from" value={staff.personnel_effective_from || ''} onChange={handleChange} sx={getTextFieldStyle()} InputLabelProps={{ shrink: true }} disabled={Boolean(id) && !isAddingNewAssignment} />
                                                {Boolean(id) && !isAddingNewAssignment && <Typography variant="caption" sx={{ color: '#94a3b8', mt: 1, display: 'block' }}>Click "Add New Assignment" to create a new timeline record.</Typography>}
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Organization Node</Typography>
                                                <Select fullWidth name="org_node_id" value={staff.org_node_id || ''} onChange={handleChange} sx={selectStyle} disabled={Boolean(id) && !isAddingNewAssignment}>
                                                    <MenuItem value="">Unassigned</MenuItem>
                                                    {(lookups.orgNodes || []).map(n => (
                                                        <MenuItem key={n.id} value={n.id} sx={{ pl: 2 + (n._depth || 0) * 2 }}>
                                                            {n._depth > 0 && <span style={{ color: '#94a3b8', marginRight: 4 }}>{'└'}</span>}
                                                            {n.node_name}{n.level_type ? ` (${n.level_type})` : ''}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Official Branch</Typography>
                                                <Select fullWidth name="branch_id" value={staff.branch_id || ''} onChange={handleChange} sx={selectStyle} disabled={Boolean(id) && !isAddingNewAssignment}>
                                                    <MenuItem value="">Global</MenuItem>
                                                    {(lookups.somBranches || []).map(b => <MenuItem key={b.id} value={b.id}>{b.branch_name}</MenuItem>)}
                                                </Select>
                                            </Box>
                                            <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 2' } }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Action Reason</Typography>
                                                <Select fullWidth name="assignment_reason" value={staff.assignment_reason || 'CORRECTION'} onChange={handleChange} sx={selectStyle} disabled={Boolean(id) && !isAddingNewAssignment}>
                                                    <MenuItem value="ONBOARDING">Onboarding</MenuItem>
                                                    <MenuItem value="CONFIRMATION">Confirmation</MenuItem>
                                                    <MenuItem value="PROMOTION">Promotion</MenuItem>
                                                    <MenuItem value="TRANSFER">Transfer</MenuItem>
                                                    <MenuItem value="REDESIGNATION">Redesignation</MenuItem>
                                                    <MenuItem value="DEMOTION">Demotion</MenuItem>
                                                    <MenuItem value="CORRECTION">Correction / Update</MenuItem>
                                                    <MenuItem value="RESIGNATION">Resignation</MenuItem>
                                                    <MenuItem value="TERMINATION">Termination</MenuItem>
                                                    <MenuItem value="CESSATION">Cessation</MenuItem>
                                                </Select>
                                            </Box>
                                            <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 2' } }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Assignment Notes</Typography>
                                                <TextField fullWidth multiline rows={2} name="assignment_notes" value={staff.assignment_notes || ''} onChange={handleChange} sx={getTextFieldStyle()} placeholder="Enter brief details about this change..." disabled={Boolean(id) && !isAddingNewAssignment} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Department</Typography>
                                                <TextField fullWidth name="department" value={staff.department} onChange={handleChange} sx={getTextFieldStyle()} placeholder="Optional legacy label" disabled={Boolean(id) && !isAddingNewAssignment} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Employee ID</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="employee_number"
                                                    inputProps={{ 'aria-label': 'Employee ID' }}
                                                    placeholder="e.g. EMP-1002"
                                                    value={staff.employee_number}
                                                    onChange={handleChange}
                                                    helperText="ID for the current employment period. Assign a new one when the person rejoins."
                                                    sx={getTextFieldStyle()}
                                                    disabled={Boolean(id) && !isAddingNewAssignment}
                                                />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Job Family</Typography>
                                                <Select fullWidth name="job_id" value={staff.job_id || ''} onChange={handleChange} sx={selectStyle} disabled={Boolean(id) && !isAddingNewAssignment}>
                                                    <MenuItem value="">Choose Family</MenuItem>
                                                    {(lookups.jobs || []).map(j => <MenuItem key={j.id} value={j.id}>{j.job_title}</MenuItem>)}
                                                </Select>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Job Title</Typography>
                                                <Select fullWidth name="job_role_id" value={staff.job_role_id || ''} onChange={handleChange} sx={selectStyle} disabled={(!staff.job_id) || (Boolean(id) && !isAddingNewAssignment)}>
                                                    <MenuItem value="">Choose Title</MenuItem>
                                                    {(lookups.jobRoles || []).filter(r => String(r.job_id) === String(staff.job_id)).map(r => <MenuItem key={r.id} value={r.id}>{r.role_name}</MenuItem>)}
                                                </Select>
                                            </Box>
                                        </FormSection>
                                        <FormSection title="Employment Details" icon={WorkIcon}>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Employment Status</Typography>
                                                <Select fullWidth name="employment_status" value={staff.employment_status} onChange={handleChange} sx={selectStyle}>
                                                    {['PROBATION', 'PERMANENT', 'CONTRACT', 'RESIGNED', 'TERMINATED'].map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                                </Select>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Joining Date</Typography>
                                                <TextField fullWidth type="date" name="joining_date" value={staff.joining_date || ''} onChange={handleChange} sx={getTextFieldStyle()} InputLabelProps={{ shrink: true }} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Confirmation Date</Typography>
                                                <TextField fullWidth type="date" name="confirmation_date" value={staff.confirmation_date || ''} onChange={handleChange} sx={getTextFieldStyle()} InputLabelProps={{ shrink: true }} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Grade Level</Typography>
                                                <Select fullWidth name="grade_id" value={staff.grade_id || ''} onChange={handleChange} sx={selectStyle}>
                                                    <MenuItem value="">None</MenuItem>
                                                    {(lookups.grades || []).map(g => <MenuItem key={g.id} value={g.id}>{g.grade_name}</MenuItem>)}
                                                </Select>
                                            </Box>
                                            {staff.employment_status === 'RESIGNED' || staff.employment_status === 'TERMINATED' ? (
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Cessation Date</Typography>
                                                    <TextField fullWidth type="date" name="cessation_date" value={staff.cessation_date || ''} onChange={handleChange} sx={getTextFieldStyle()} InputLabelProps={{ shrink: true }} />
                                                </Box>
                                            ) : null}
                                        </FormSection>
                                    </>
                                )}

                                {selectedSection === 'access' && (
                                    <FormSection title="System Credentials" icon={SecurityIcon}>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#475569' }}>System Username *</Typography>
                                            <TextField
                                                fullWidth name="username" required inputProps={{ 'aria-label': 'Username' }} value={staff.username}
                                                onChange={handleChange}
                                                error={touched.username && !!errors.username}
                                                disabled={isRehireMode}
                                                helperText={touched.username && errors.username ? errors.username : (isRehireMode ? 'Existing account will be reused' : '')}
                                                sx={getTextFieldStyle(touched.username, errors.username)}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#475569' }}>Work Email</Typography>
                                            <TextField
                                                fullWidth name="email" value={staff.email}
                                                onChange={handleChange}
                                                error={touched.email && !!errors.email}
                                                disabled={isRehireMode}
                                                helperText={touched.email && errors.email ? errors.email : (isRehireMode ? 'Existing email will be kept' : '')}
                                                sx={getTextFieldStyle(touched.email, errors.email)}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#475569' }}>System Role</Typography>
                                            <Select fullWidth name="role" value={staff.role} onChange={handleChange} displayEmpty sx={selectStyle}>
                                                <MenuItem value="" disabled>Select Role</MenuItem>
                                                <MenuItem value="hr_manager">HR Manager (Global + SOM)</MenuItem>
                                                <MenuItem value="hr">HR Professional (Global)</MenuItem>
                                                <MenuItem value="hr_branch">Branch HR (Scoped)</MenuItem>
                                                <MenuItem value="manager">Manager (Scoped)</MenuItem>
                                                <MenuItem value="management_viewer">Management Viewer (Read Only)</MenuItem>
                                                <MenuItem value="staff">Standard User</MenuItem>
                                                <MenuItem value="admin">Administrator</MenuItem>
                                            </Select>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#475569' }}>Operational Status</Typography>
                                            <Select fullWidth name="is_active" value={staff.is_active} onChange={handleChange} sx={selectStyle}>
                                                <MenuItem value={1}>Active Profile</MenuItem>
                                                <MenuItem value={0}>Inactive / Suspended</MenuItem>
                                            </Select>
                                        </Box>

                                        {!id && (
                                            isRehireMode ? (
                                                <Box sx={{
                                                    gridColumn: '1 / -1', p: 2.5, borderRadius: 3,
                                                    bgcolor: '#ede9fe', border: '1px solid #c4b5fd'
                                                }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#5b21b6', mb: 0.5 }}>
                                                        🔑 Password Auto-Reset
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#6d28d9' }}>
                                                        The staff's password will be automatically reset to the <strong>last 6 digits of their NRC number</strong>.
                                                        They must change it on first login.
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <>
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#475569' }}>Initial Password *</Typography>
                                                        <TextField
                                                            fullWidth name="password" type={showPassword ? 'text' : 'password'} placeholder="Min 6 characters" value={staff.password} onChange={handleChange} autoComplete="new-password"
                                                            error={touched.password && !!errors.password} helperText={touched.password && errors.password} sx={getTextFieldStyle(touched.password, errors.password)}
                                                            InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}</IconButton></InputAdornment> }}
                                                        />
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#475569' }}>Confirm Password *</Typography>
                                                        <TextField
                                                            fullWidth name="confirmPassword" type={showPassword ? 'text' : 'password'} placeholder="Repeat password" value={confirmPassword} onChange={(e) => {
                                                                setConfirmPassword(e.target.value);
                                                                setTouched(prev => ({ ...prev, confirmPassword: true }));
                                                            }}
                                                            error={touched.confirmPassword && !!errors.confirmPassword} helperText={touched.confirmPassword && errors.confirmPassword} sx={getTextFieldStyle(touched.confirmPassword, errors.confirmPassword)}
                                                        />
                                                    </Box>
                                                </>
                                            )
                                        )}
                                        {id && (
                                            <Box sx={{ gridColumn: '1 / -1' }}>
                                                <Alert severity="info" sx={{ borderRadius: 3 }}>
                                                    Passwords can only be reset from the Staff Profile view for existing accounts.
                                                </Alert>
                                            </Box>
                                        )}
                                        <Box sx={{ gridColumn: '1 / -1', mt: 3 }}>
                                            <FormSection title="Approvers & Policies" icon={VerifiedUserIcon}>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Work Modality</Typography>
                                                    <Select fullWidth name="work_type" value={staff.work_type} onChange={handleChange} sx={selectStyle}>
                                                        <MenuItem value="office">Standard Office</MenuItem>
                                                        <MenuItem value="shift_based">Shift Based</MenuItem>
                                                    </Select>
                                                </Box>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Reporting Supervisor (L1)</Typography>
                                                    <Select fullWidth name="level1_approver_id" value={staff.level1_approver_id || ''} onChange={handleChange} sx={selectStyle}>
                                                        <MenuItem value="">None</MenuItem>
                                                        {(lookups.managers || []).map(u => <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>)}
                                                    </Select>
                                                </Box>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>HR Approver (L2)</Typography>
                                                    <Select fullWidth name="level2_approver_id" value={staff.level2_approver_id || ''} onChange={handleChange} sx={selectStyle}>
                                                        <MenuItem value="">None</MenuItem>
                                                        {(lookups.hrs || []).map(u => <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>)}
                                                    </Select>
                                                </Box>
                                                <Box sx={{ gridColumn: '1 / -1' }}>
                                                    <Box sx={{ p: 2, borderRadius: 3, border: '1.5px solid #f1f5f9', bgcolor: '#f8fafc' }}>
                                                        <Stack direction="row" spacing={4}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                                                <Box sx={{ mr: 2 }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>Auto-Approve Attendance</Typography>
                                                                    <Typography variant="caption" sx={{ color: '#64748b' }}>If enabled, manual entries won't require approval flow.</Typography>
                                                                </Box>
                                                                <Switch checked={staff.auto_approve_manual_attendance === 1} onChange={(e) => setStaff(prev => ({ ...prev, auto_approve_manual_attendance: e.target.checked ? 1 : 0 }))} color="success" />
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                                                <Box sx={{ mr: 2 }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>Require Attendance Photo</Typography>
                                                                    <Typography variant="caption" sx={{ color: '#64748b' }}>Capture photo during clock in/out.</Typography>
                                                                </Box>
                                                                <Switch checked={staff.is_photo_required !== 0} onChange={(e) => setStaff(prev => ({ ...prev, is_photo_required: e.target.checked ? 1 : 0 }))} color="success" />
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                                                <Box sx={{ mr: 2 }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>Overtime Eligibility</Typography>
                                                                    <Typography variant="caption" sx={{ color: '#64748b' }}>Allow staff to claim OT hours.</Typography>
                                                                </Box>
                                                                <Switch
                                                                    checked={staff.ot_eligible === 1}
                                                                    onChange={(e) => setStaff(prev => ({
                                                                        ...prev,
                                                                        ot_eligible: e.target.checked ? 1 : 0,
                                                                        ot_policy_code: e.target.checked ? prev.ot_policy_code : 'NONE'
                                                                    }))}
                                                                    color="success"
                                                                />
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                                                <Box sx={{ mr: 2 }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>Payroll Exclusion</Typography>
                                                                    <Typography variant="caption" sx={{ color: '#64748b' }}>Bypass staff from payroll checks.</Typography>
                                                                </Box>
                                                                <Switch
                                                                    checked={staff.exclude_from_payroll === 1}
                                                                    onChange={(e) => setStaff(prev => ({
                                                                        ...prev,
                                                                        exclude_from_payroll: e.target.checked ? 1 : 0
                                                                    }))}
                                                                    color="warning"
                                                                />
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                </Box>
                                                {staff.ot_eligible === 1 && (
                                                    <>
                                                        <Box>
                                                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>OT Policy</Typography>
                                                            <Select fullWidth name="ot_policy_code" value={staff.ot_policy_code} onChange={handleChange} sx={selectStyle}>
                                                                <MenuItem value="MANUAL_CLAIM">Manual Claims</MenuItem>
                                                                <MenuItem value="AUTO_CAPTURE">Auto Capture and Auto Calculate</MenuItem>
                                                                <MenuItem value="AUTO_APPROVE">Auto Capture and Approved</MenuItem>
                                                                <MenuItem value="NONE">No OT</MenuItem>
                                                            </Select>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Rate Profile</Typography>
                                                            <Select fullWidth name="ot_rate_profile" value={staff.ot_rate_profile} onChange={handleChange} sx={selectStyle}>
                                                                {(lookups.otRateProfiles || []).map(p => (
                                                                    <MenuItem key={p.id} value={p.profile_code}>
                                                                        {p.profile_code} - {p.profile_name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </Box>
                                                    </>
                                                )}
                                            </FormSection>
                                        </Box>
                                    </FormSection>
                                )}

                                {selectedSection === 'schedule' && (
                                    <FormSection title="Roster & Attendance Configuration" icon={ScheduleIcon}>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Shift Group (Base Assignment)</Typography>
                                            <Select fullWidth name="sm_group_id" value={staff.sm_group_id || ''} onChange={handleChange} sx={selectStyle}>
                                                <MenuItem value="">Unassigned</MenuItem>
                                                {(lookups.smGroups || []).map(g => <MenuItem key={g.id} value={g.id}>{g.group_name}</MenuItem>)}
                                            </Select>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Default Work Pattern</Typography>
                                            <Select fullWidth name="sm_pattern_id" value={staff.sm_pattern_id || ''} onChange={handleChange} sx={selectStyle}>
                                                <MenuItem value="">No Pattern</MenuItem>
                                                {(lookups.smPatterns || []).map(p => <MenuItem key={p.id} value={p.id}>{p.pattern_name}</MenuItem>)}
                                            </Select>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Late Policy Rule</Typography>
                                            <Select fullWidth name="late_policy_id" value={staff.late_policy_id || ''} onChange={handleChange} sx={selectStyle}>
                                                <MenuItem value="">Standard No-Penalty</MenuItem>
                                                {(lookups.latePolicies || []).map(p => <MenuItem key={p.id} value={p.id}>{p.policy_name}</MenuItem>)}
                                            </Select>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Duty Configuration Effective From</Typography>
                                            <TextField fullWidth type="date" name="sm_effective_from" value={staff.sm_effective_from || ''} onChange={handleChange} sx={selectStyle} InputLabelProps={{ shrink: true }} />
                                            <Typography variant="caption" sx={{ color: '#94a3b8', mt: 1, display: 'block' }}>Specifies when the above Shift Group and Pattern assignments take effect. Set to an earlier date to fix historical roster issues.</Typography>
                                        </Box>
                                        <Box sx={{ gridColumn: '1 / -1' }}>
                                            <Box sx={{ p: 2, borderRadius: 3, border: '1.5px solid #f1f5f9', bgcolor: '#f8fafc', mb: 2 }}>
                                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>Allow Split Shift</Typography>
                                                        <Typography variant="caption" sx={{ color: '#64748b' }}>Permit multiple roster shifts in one day for this office staff. Assign actual split shifts in Roster Planner.</Typography>
                                                    </Box>
                                                    <Switch
                                                        checked={staff.allow_split_shift === 1}
                                                        onChange={(e) => setStaff(prev => ({ ...prev, allow_split_shift: e.target.checked ? 1 : 0 }))}
                                                        color="success"
                                                        disabled={staff.work_type !== 'office'}
                                                    />
                                                </Stack>
                                            </Box>
                                        </Box>
                                        <Box sx={{ gridColumn: '1 / -1' }}>
                                            <Box sx={{ p: 2, borderRadius: 3, border: '1.5px solid #f1f5f9', bgcolor: '#f8fafc', mb: 2 }}>
                                                <Stack spacing={2}>
                                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>Allow Web Clock</Typography>
                                                            <Typography variant="caption" sx={{ color: '#64748b' }}>Permit this staff to use the browser web-clock page instead of the mobile app.</Typography>
                                                        </Box>
                                                        <Switch
                                                            checked={staff.web_clock_allowed === 1}
                                                            onChange={(e) => setStaff(prev => ({
                                                                ...prev,
                                                                web_clock_allowed: e.target.checked ? 1 : 0,
                                                                web_geofence_required: e.target.checked ? prev.web_geofence_required : 0,
                                                            }))}
                                                            color="success"
                                                        />
                                                    </Stack>
                                                    <Divider sx={{ borderStyle: 'dashed' }} />
                                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>Require Web Geo-Fence</Typography>
                                                            <Typography variant="caption" sx={{ color: '#64748b' }}>Require browser clock-in/out to be within an assigned geo-access boundary.</Typography>
                                                        </Box>
                                                        <Switch
                                                            checked={staff.web_geofence_required === 1}
                                                            onChange={(e) => setStaff(prev => ({ ...prev, web_geofence_required: e.target.checked ? 1 : 0 }))}
                                                            color="success"
                                                            disabled={staff.web_clock_allowed !== 1}
                                                        />
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#475569' }}>Geo-Access Boundaries</Typography>
                                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                                {(lookups.locations || []).map(loc => (
                                                    <Button
                                                        key={loc.id} size="small" variant={staff.allowed_locations.includes(loc.location_name) ? 'contained' : 'outlined'}
                                                        sx={{ py: 1, px: 2, borderRadius: 2, textTransform: 'none', border: '1.5px solid', borderColor: staff.allowed_locations.includes(loc.location_name) ? 'primary.main' : '#e2e8f0', bgcolor: staff.allowed_locations.includes(loc.location_name) ? 'primary.main' : 'transparent', color: staff.allowed_locations.includes(loc.location_name) ? '#fff' : '#64748b' }}
                                                        onClick={() => {
                                                            const active = staff.allowed_locations.includes(loc.location_name);
                                                            setStaff(prev => ({ ...prev, allowed_locations: active ? prev.allowed_locations.filter(l => l !== loc.location_name) : [...prev.allowed_locations, loc.location_name] }));
                                                        }}
                                                    >
                                                        {loc.location_name}
                                                    </Button>
                                                ))}
                                            </Stack>
                                        </Box>
                                    </FormSection>
                                )}

                                {selectedSection === 'qualifications' && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2, borderBottom: '1px solid #f1f5f9' }}>
                                            <Box>
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <Box sx={{ width: 40, height: 40, borderRadius: 2.5, bgcolor: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4338ca' }}>
                                                        <SchoolIcon />
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                                            Education & Qualifications
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                                                            {id ? 'Qualification changes save immediately and are not undone by Cancel.' : 'Enter educational credentials provided by the candidate during intake.'}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </Box>
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={handleOpenAddQualification}
                                                sx={{
                                                    bgcolor: 'primary.main',
                                                    borderRadius: 2.5,
                                                    px: 3,
                                                    py: 1,
                                                    fontWeight: 700,
                                                    textTransform: 'none',
                                                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
                                                    '&:hover': { bgcolor: 'primary.dark' }
                                                }}
                                            >
                                                Add Qualification
                                            </Button>
                                        </Box>

                                        {qualifications.length === 0 ? (
                                            <Box sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: '#f8fafc' }}>
                                                <SchoolIcon sx={{ fontSize: 52, color: '#94a3b8', mb: 1.5 }} />
                                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#334155', mb: 0.5 }}>
                                                    No Qualifications Added Yet
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#64748b', mb: 3, maxWidth: 460, mx: 'auto' }}>
                                                    {id ? 'This employee has no qualifications recorded.' : 'You can optionally add the candidate\'s degrees, diplomas, or certifications now, or add them later directly on their profile.'}
                                                </Typography>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<AddIcon />}
                                                    onClick={handleOpenAddQualification}
                                                    sx={{ borderRadius: 2.5, fontWeight: 700, textTransform: 'none', px: 3 }}
                                                >
                                                    Add First Qualification
                                                </Button>
                                            </Box>
                                        ) : (
                                            <>
                                                <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                                                    <Chip
                                                        icon={<SchoolIcon />}
                                                        label={`Total: ${qualifications.length}`}
                                                        sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }}
                                                    />
                                                    <Chip
                                                        label={`Academic: ${qualifications.filter(q => (q.category || 'ACADEMIC') === 'ACADEMIC').length}`}
                                                        sx={{ fontWeight: 700, bgcolor: '#e0e7ff', color: '#3730a3' }}
                                                    />
                                                    <Chip
                                                        label={`Cert / Diploma: ${qualifications.filter(q => ['CERTIFICATION', 'DIPLOMA'].includes(q.category)).length}`}
                                                        sx={{ fontWeight: 700, bgcolor: '#dcfce7', color: '#166534' }}
                                                    />
                                                    {qualifications.some(q => Boolean(q.is_overseas)) && (
                                                        <Chip
                                                            icon={<PublicIcon fontSize="small" />}
                                                            label={`Overseas: ${qualifications.filter(q => Boolean(q.is_overseas)).length}`}
                                                            sx={{ fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }}
                                                        />
                                                    )}
                                                </Stack>

                                                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                                    <Table size="small">
                                                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 800, color: '#475569', py: 1.8 }}>Category</TableCell>
                                                                <TableCell sx={{ fontWeight: 800, color: '#475569', py: 1.8 }}>Level & Major</TableCell>
                                                                <TableCell sx={{ fontWeight: 800, color: '#475569', py: 1.8 }}>Institution / University</TableCell>
                                                                <TableCell sx={{ fontWeight: 800, color: '#475569', py: 1.8 }}>Period</TableCell>
                                                                <TableCell sx={{ fontWeight: 800, color: '#475569', py: 1.8 }}>Status & Grade</TableCell>
                                                                <TableCell align="right" sx={{ fontWeight: 800, color: '#475569', py: 1.8 }}>Actions</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {qualifications.map((q, idx) => {
                                                                const catStyle = categoryColors[q.category] || categoryColors.OTHER;
                                                                const statStyle = statusColors[q.status] || statusColors.COMPLETED;
                                                                return (
                                                                    <TableRow key={q.id || idx} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                                        <TableCell>
                                                                            <Chip
                                                                                size="small"
                                                                                label={catStyle.label}
                                                                                sx={{ bgcolor: catStyle.bg, color: catStyle.text, fontWeight: 700, fontSize: '0.75rem' }}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                                                                {q.level}
                                                                            </Typography>
                                                                            {(q.major_subject || q.field_of_study) && (
                                                                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                                                                    {q.major_subject || q.field_of_study}
                                                                                    {q.major_subject && q.field_of_study ? ` (${q.field_of_study})` : ''}
                                                                                </Typography>
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                                                                                {q.institution_name}
                                                                            </Typography>
                                                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                                                    {q.country || 'Myanmar'}
                                                                                </Typography>
                                                                                {Boolean(q.is_overseas) && (
                                                                                    <Chip size="small" icon={<PublicIcon sx={{ fontSize: '12px !important' }} />} label="Overseas" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }} />
                                                                                )}
                                                                            </Stack>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.825rem' }}>
                                                                                {q.start_date ? toInputDate(q.start_date) : ''}
                                                                                {q.start_date && q.end_date ? ' ~ ' : ''}
                                                                                {q.end_date ? toInputDate(q.end_date) : (!q.start_date ? '—' : 'Present')}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                                <Chip
                                                                                    size="small"
                                                                                    label={statStyle.label}
                                                                                    sx={{ bgcolor: statStyle.bg, color: statStyle.text, fontWeight: 700, fontSize: '0.725rem' }}
                                                                                />
                                                                                {(q.grade_result || q.cgpa) && (
                                                                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>
                                                                                        {q.grade_result}{q.grade_result && q.cgpa ? ' • ' : ''}{q.cgpa ? `GPA ${q.cgpa}` : ''}
                                                                                    </Typography>
                                                                                )}
                                                                            </Stack>
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            <Tooltip title="Edit Qualification">
                                                                                <IconButton size="small" onClick={() => handleOpenEditQualification(q, idx)} sx={{ color: '#64748b', '&:hover': { color: 'primary.main' } }}>
                                                                                    <EditIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                            <Tooltip title="Delete Qualification">
                                                                                <IconButton size="small" color="error" onClick={() => handleDeleteQualification(idx, q)}>
                                                                                    <DeleteIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </>
                                        )}
                                    </Box>
                                )}

                                {showPayrollTab && selectedSection === 'payroll' && (
                                    <>
                                        {!hasActiveAssignment ? (
                                            <Alert severity="warning" sx={{ mb: 2 }}>
                                                You must create an Assignment for this employee before entering payroll data.
                                            </Alert>
                                        ) : staff.has_pending_payroll ? (
                                            <Alert
                                                severity="warning"
                                                sx={{ mb: 2 }}
                                                action={
                                                    <Button color="inherit" size="small" onClick={() => navigate('/payroll/pending-setups')} sx={{ fontWeight: 700 }}>
                                                        Open Pending Setups
                                                    </Button>
                                                }
                                            >
                                                Payroll modifications are currently locked because there is a pending payroll setup for this staff's recent assignment. Please complete the setup first.
                                            </Alert>
                                        ) : (
                                            <Alert severity="info" sx={{ mb: 2 }}>
                                                This tab changes compensation only. Promotions and transfers must first be created in Assignment, then completed from Payroll → Pending Payroll Setups.
                                            </Alert>
                                        )}
                                        {hasActiveAssignment && (
                                            <fieldset disabled={staff.has_pending_payroll} style={{ border: 'none', margin: 0, padding: 0 }}>
                                            <FormSection title="Salary Structure" icon={AssessmentIcon}>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Basic Salary</Typography>
                                                <TextField
                                                    fullWidth
                                                    type="number"
                                                    name="basic_salary"
                                                    value={staff.basic_salary}
                                                    onChange={handleChange}
                                                    sx={getTextFieldStyle()}
                                                    error={!!errors.basic_salary}
                                                    helperText={errors.basic_salary}
                                                />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Calculation Basis</Typography>
                                                <Select fullWidth name="calculation_basis_id" value={staff.calculation_basis_id || ''} onChange={handleChange} sx={selectStyle}>
                                                    {(lookups.calcBases || []).map(b => <MenuItem key={b.id} value={b.id}>{b.basis_name}</MenuItem>)}
                                                </Select>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Payroll Group</Typography>
                                                <Select fullWidth name="payroll_group_id" value={staff.payroll_group_id || ''} onChange={handleChange} sx={selectStyle}>
                                                    <MenuItem value=""><em>None</em></MenuItem>
                                                    {(lookups.payrollGroups || []).filter(g => Number(g.is_active) === 1).map(g => <MenuItem key={g.id} value={g.id}>{g.group_name}</MenuItem>)}
                                                </Select>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Effective From</Typography>
                                                <TextField fullWidth type="date" name="effective_from_payroll" value={staff.effective_from_payroll || ''} onChange={handleChange} sx={getTextFieldStyle()} InputLabelProps={{ shrink: true }} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Action Reason</Typography>
                                                <Select fullWidth name="payroll_change_reason" value={staff.payroll_change_reason || 'CORRECTION'} onChange={handleChange} sx={selectStyle}>
                                                    <MenuItem value="INITIAL_SETUP">Initial Payroll Setup</MenuItem>
                                                    <MenuItem value="PROMOTION">Promotion</MenuItem>
                                                    <MenuItem value="TRANSFER">Transfer</MenuItem>
                                                    <MenuItem value="INCREMENT">Increment</MenuItem>
                                                    <MenuItem value="DECREMENT">Decrement</MenuItem>
                                                    <MenuItem value="CORRECTION">Payroll Correction</MenuItem>
                                                </Select>
                                            </Box>
                                        </FormSection>
                                        <FormSection title="Statutory Calculations" icon={VerifiedUserIcon}>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#475569' }}>Calculate SSB</Typography>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Switch
                                                        checked={Number(staff.ssb_enabled) === 1}
                                                        onChange={(e) => { setPayrollDirty(true); setStaff(prev => ({ ...prev, ssb_enabled: e.target.checked ? 1 : 0 })); }}
                                                    />
                                                    <Typography variant="body2" color="text.secondary">{Number(staff.ssb_enabled) === 1 ? 'Enabled' : 'Disabled'}</Typography>
                                                </Stack>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#475569' }}>Calculate Personal Income Tax</Typography>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Switch
                                                        checked={Number(staff.pit_enabled) === 1}
                                                        onChange={(e) => { setPayrollDirty(true); setStaff(prev => ({ ...prev, pit_enabled: e.target.checked ? 1 : 0 })); }}
                                                    />
                                                    <Typography variant="body2" color="text.secondary">{Number(staff.pit_enabled) === 1 ? 'Enabled' : 'Disabled'}</Typography>
                                                </Stack>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>SSB Scheme</Typography>
                                                <Select fullWidth name="ssb_scheme_override" value={staff.ssb_scheme_override || 'INHERIT'} onChange={handleChange} sx={selectStyle}>
                                                    <MenuItem value="INHERIT">Use Company Policy</MenuItem>
                                                    <MenuItem value="STANDARD">Standard Scheme</MenuItem>
                                                    <MenuItem value="OVER_60_REGISTERED">Over-60 Registered Scheme</MenuItem>
                                                </Select>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Change Reason</Typography>
                                                <TextField fullWidth name="statutory_reason" value={staff.statutory_reason || ''} onChange={handleChange} sx={getTextFieldStyle()} placeholder="Reason for participation or scheme change" />
                                            </Box>
                                        </FormSection>
                                        <FormSection title="Recurring Add-ons & Deducts" icon={AddIcon}>
                                            <Box sx={{ gridColumn: '1 / -1' }}>
                                                {(staff.recurring_elements || []).map((el, i) => (
                                                    <Box key={i} sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2, mb: 2, alignItems: 'center' }}>
                                                        <Box>
                                                            <Select fullWidth value={el.element_id || ''} sx={selectStyle} onChange={(e) => {
                                                                const nr = [...staff.recurring_elements]; nr[i].element_id = e.target.value;
                                                                setPayrollDirty(true); setStaff(prev => ({ ...prev, recurring_elements: nr }));
                                                            }}>
                                                                {(lookups.payrollElements || []).map(p => <MenuItem key={p.id} value={p.id}>{p.element_name}</MenuItem>)}
                                                            </Select>
                                                        </Box>
                                                        <Box>
                                                            <TextField fullWidth type="number" value={el.amount || 0} sx={getTextFieldStyle()} onChange={(e) => {
                                                                const nr = [...staff.recurring_elements]; nr[i].amount = e.target.value;
                                                                setPayrollDirty(true); setStaff(prev => ({ ...prev, recurring_elements: nr }));
                                                            }} />
                                                        </Box>
                                                        <Box>
                                                            <TextField
                                                                fullWidth type="date" value={toInputDate(el.effective_from) || ''}
                                                                label="Start Date" InputLabelProps={{ shrink: true }} sx={getTextFieldStyle()}
                                                                onChange={(e) => {
                                                                    const nr = [...staff.recurring_elements]; nr[i].effective_from = e.target.value;
                                                                    setPayrollDirty(true); setStaff(prev => ({ ...prev, recurring_elements: nr }));
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box>
                                                            <TextField
                                                                fullWidth type="date" value={toInputDate(el.effective_to) || ''}
                                                                label="End Date (Optional)" InputLabelProps={{ shrink: true }} sx={getTextFieldStyle()}
                                                                onChange={(e) => {
                                                                    const nr = [...staff.recurring_elements]; nr[i].effective_to = e.target.value;
                                                                    setPayrollDirty(true); setStaff(prev => ({ ...prev, recurring_elements: nr }));
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                            <IconButton color="error" onClick={() => { setPayrollDirty(true); setStaff(prev => ({ ...prev, recurring_elements: prev.recurring_elements.filter((_, idx) => idx !== i) })); }}><DeleteIcon /></IconButton>
                                                        </Box>
                                                    </Box>
                                                ))}
                                                <Button startIcon={<AddIcon />} variant="text" onClick={() => { setPayrollDirty(true); setStaff(prev => ({ ...prev, recurring_elements: [...prev.recurring_elements, { element_id: '', amount: 0, effective_from: toInputDate(staff.effective_from_payroll || new Date()), effective_to: null }] })); }} sx={{ fontWeight: 700, color: 'primary.main', textTransform: 'none' }}>Add Payroll Element</Button>
                                            </Box>
                                        </FormSection>
                                        </fieldset>
                                        )}
                                    </>
                                )}

                                {selectedSection === 'documents' && (
                                    <Box sx={{ p: 4, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4 }}>
                                        <UploadIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                                        <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 600 }}>Documents can be uploaded after profile creation in the "Documents" tab.</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>

                        {/* Qualification Add/Edit Dialog */}
                        <Dialog open={qualificationDialogOpen} onClose={() => setQualificationDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
                            <DialogTitle sx={{ fontWeight: 800 }}>
                                {editingQualIndex !== null || editingQualId ? 'Edit Qualification' : 'Add Qualification'}
                            </DialogTitle>
                            <DialogContent>
                                <Stack spacing={2.5} sx={{ mt: 1 }}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Credential Category"
                                            value={qualificationForm.category}
                                            onChange={(e) => setQualificationForm({ ...qualificationForm, category: e.target.value })}
                                            required
                                        >
                                            <MenuItem value="ACADEMIC">Academic Degree / Schooling</MenuItem>
                                            <MenuItem value="DIPLOMA">Diploma / Polytechnic</MenuItem>
                                            <MenuItem value="CERTIFICATION">Professional Certification</MenuItem>
                                            <MenuItem value="TRAINING">Training / Course</MenuItem>
                                            <MenuItem value="LICENSE">Professional License</MenuItem>
                                            <MenuItem value="OTHER">Other</MenuItem>
                                        </TextField>
                                        <TextField
                                            fullWidth
                                            label="Qualification Level"
                                            placeholder="e.g. Bachelor, Master, Diploma, Certificate"
                                            value={qualificationForm.level}
                                            onChange={(e) => setQualificationForm({ ...qualificationForm, level: e.target.value })}
                                            required
                                        />
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                        <TextField
                                            fullWidth
                                            label="Major / Subject / Course"
                                            placeholder="e.g. Computer Science, Accounting"
                                            value={qualificationForm.major_subject}
                                            onChange={(e) => setQualificationForm({ ...qualificationForm, major_subject: e.target.value })}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Field of Study"
                                            placeholder="e.g. Science, IT, Engineering"
                                            value={qualificationForm.field_of_study}
                                            onChange={(e) => setQualificationForm({ ...qualificationForm, field_of_study: e.target.value })}
                                        />
                                    </Box>

                                    <TextField
                                        fullWidth
                                        label="Institution / University / Provider"
                                        placeholder="e.g. University of Yangon, ACCA Global"
                                        value={qualificationForm.institution_name}
                                        onChange={(e) => setQualificationForm({ ...qualificationForm, institution_name: e.target.value })}
                                        required
                                    />

                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="Start Date"
                                            InputLabelProps={{ shrink: true }}
                                            value={qualificationForm.start_date}
                                            onChange={(e) => setQualificationForm({ ...qualificationForm, start_date: e.target.value })}
                                        />
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="End Date / Completion Date"
                                            InputLabelProps={{ shrink: true }}
                                            value={qualificationForm.end_date}
                                            onChange={(e) => setQualificationForm({ ...qualificationForm, end_date: e.target.value })}
                                        />
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Status"
                                            value={qualificationForm.status}
                                            onChange={(e) => setQualificationForm({ ...qualificationForm, status: e.target.value })}
                                        >
                                            <MenuItem value="COMPLETED">Completed</MenuItem>
                                            <MenuItem value="PURSUING">Currently Pursuing</MenuItem>
                                            <MenuItem value="INCOMPLETE">Incomplete / Dropped</MenuItem>
                                        </TextField>
                                        <TextField
                                            fullWidth
                                            label="Result / Grade"
                                            placeholder="e.g. Distinction, Credit, Pass"
                                            value={qualificationForm.grade_result}
                                            onChange={(e) => setQualificationForm({ ...qualificationForm, grade_result: e.target.value })}
                                        />
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            inputProps={{ step: "0.01", min: "0", max: "5" }}
                                            label="CGPA / GPA"
                                            placeholder="e.g. 3.85"
                                            value={qualificationForm.cgpa}
                                            onChange={(e) => setQualificationForm({ ...qualificationForm, cgpa: e.target.value })}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Credential ID / License No"
                                            placeholder="e.g. CERT-2023-9988"
                                            value={qualificationForm.credential_id}
                                            onChange={(e) => setQualificationForm({ ...qualificationForm, credential_id: e.target.value })}
                                        />
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, alignItems: 'center' }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={qualificationForm.is_overseas}
                                                    onChange={(e) => setQualificationForm({ ...qualificationForm, is_overseas: e.target.checked })}
                                                />
                                            }
                                            label="Studied Overseas"
                                        />
                                        <TextField
                                            fullWidth
                                            label="Country"
                                            value={qualificationForm.country}
                                            onChange={(e) => setQualificationForm({ ...qualificationForm, country: e.target.value })}
                                        />
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, alignItems: 'center' }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={qualificationForm.has_expiry}
                                                    onChange={(e) => setQualificationForm({ ...qualificationForm, has_expiry: e.target.checked })}
                                                />
                                            }
                                            label="Has Expiry Date"
                                        />
                                        {qualificationForm.has_expiry && (
                                            <TextField
                                                fullWidth
                                                type="date"
                                                label="Expiry Date"
                                                InputLabelProps={{ shrink: true }}
                                                value={qualificationForm.expiry_date}
                                                onChange={(e) => setQualificationForm({ ...qualificationForm, expiry_date: e.target.value })}
                                            />
                                        )}
                                    </Box>

                                    <TextField
                                        fullWidth
                                        label="Remarks / Notes"
                                        multiline
                                        rows={2}
                                        value={qualificationForm.remarks}
                                        onChange={(e) => setQualificationForm({ ...qualificationForm, remarks: e.target.value })}
                                    />
                                </Stack>
                            </DialogContent>
                            <DialogActions sx={{ p: 2.5 }}>
                                <Button onClick={() => setQualificationDialogOpen(false)} sx={{ fontWeight: 700, color: '#64748b' }}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    disabled={qualSaving}
                                    onClick={handleSaveQualificationDialog}
                                    sx={{ borderRadius: 2, px: 3, fontWeight: 700, bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                                >
                                    {qualSaving ? 'Saving...' : (editingQualIndex !== null || editingQualId ? 'Save Changes' : (id ? 'Add Qualification' : 'Add to Staged List'))}
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </form>
                </Fade>
            </Box>

            {/* Sticky Action Footer */}
            <Paper
                elevation={0}
                sx={{
                    position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
                    width: '100%', maxWidth: 1400, boxSizing: 'border-box',
                    bgcolor: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(12px)',
                    border: '1.5px solid #f1f5f9', borderRadius: '16px 16px 0 0', p: 2, px: { xs: 2, md: 3 },
                    gap: 1.5, flexWrap: 'wrap',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)',
                    zIndex: 1000
                }}
            >
                <Box>
                    <Typography variant="caption" sx={{ color: hasUnsavedChanges ? '#b45309' : '#64748b', fontWeight: 700, display: 'block' }}>
                        {hasUnsavedChanges ? 'Unsaved changes' : id ? 'No unsaved profile changes' : 'New employee'}
                    </Typography>
                    {!id && (
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                            Section {tabValue + 1} of {formSections.length}: {sectionLabels[selectedSection]}
                        </Typography>
                    )}
                </Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: 'flex-end' }}>
                    <Button
                        variant="text" onClick={handleCancel} disabled={saving}
                        sx={{ borderRadius: 2.5, px: 2, fontWeight: 700, color: '#64748b', textTransform: 'none', '&:hover': { bgcolor: '#f8fafc' } }}
                    >
                        Cancel
                    </Button>
                    {!id && tabValue > 0 && (
                        <Button
                            variant="outlined"
                            onClick={() => setTabValue(tabValue - 1)}
                            disabled={saving}
                            sx={{ borderRadius: 2.5, px: 2, fontWeight: 700, textTransform: 'none' }}
                        >
                            Previous
                        </Button>
                    )}
                    {!id && tabValue < formSections.length - 1 && (
                        <Button
                            variant="outlined"
                            onClick={() => setTabValue(tabValue + 1)}
                            disabled={saving}
                            sx={{ borderRadius: 2.5, px: 2, fontWeight: 700, textTransform: 'none' }}
                        >
                            Next Section
                        </Button>
                    )}
                    <Button
                        onClick={() => {
                            const isOnPayrollTab = showPayrollTab && selectedSection === 'payroll';
                            if (isOnPayrollTab && staff.has_pending_payroll) {
                                // Already on payroll tab, just highlight the warning — do nothing
                                return;
                            }
                            document.getElementById('staff-form').dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                        }}
                        variant="contained"
                        disabled={saving}
                        startIcon={<SaveIcon />}
                        sx={{
                            bgcolor: (showPayrollTab && selectedSection === 'payroll' && staff.has_pending_payroll) ? '#f59e0b' : 'primary.main',
                            borderRadius: 2.5, px: { xs: 2, sm: 5 }, py: 1.2, fontWeight: 700, textTransform: 'none',
                            boxShadow: (theme) => `0 4px 12px ${(showPayrollTab && selectedSection === 'payroll' && staff.has_pending_payroll) ? '#f59e0b33' : theme.palette.primary.main + '33'}`,
                            '&:hover': { bgcolor: (showPayrollTab && selectedSection === 'payroll' && staff.has_pending_payroll) ? '#d97706' : 'primary.dark' }
                        }}
                    >
                        {saving ? 'Processing...' : ((showPayrollTab && selectedSection === 'payroll' && staff.has_pending_payroll) ? '⚠ Payroll Pending' : isAddingNewAssignment ? 'Save New Assignment' : (id ? 'Save Changes' : 'Create Employee'))}
                    </Button>
                </Stack>
            </Paper>

            <Dialog open={discardOpen} onClose={() => setDiscardOpen(false)} maxWidth="xs" fullWidth aria-labelledby="discard-profile-title">
                <DialogTitle id="discard-profile-title">Discard unsaved changes?</DialogTitle>
                <DialogContent>
                    <Typography>{id ? 'Your unsaved profile changes will be lost. Qualifications already saved will remain.' : 'Your unsaved employee information will be lost.'}</Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDiscardOpen(false)}>Keep Editing</Button>
                    <Button color="error" variant="contained" onClick={returnToEmployee}>Discard Changes</Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2, fontWeight: 600 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default StaffForm;
